import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { backendApi } from "@/api/backendApi";
import { 
  Search, 
  MapPin, 
  Briefcase, 
  Building2, 
  IndianRupee, 
  Clock, 
  ChevronRight, 
  Filter,
  Bookmark,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";

export default function JobsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for filters
  const [keyword, setKeyword] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [jobType, setJobType] = useState("all");
  
  const itemsPerPage = 10;

  // Sync with URL params on mount (e.g. from Navbar search)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const kw = params.get("keyword");
    const loc = params.get("location");
    if (kw) setKeyword(kw);
    if (loc) setLocationFilter(loc);
  }, [location.search]);

  // Fetch jobs using React Query
  const { data: jobs = [], isLoading, isFetching } = useQuery({
    queryKey: ["jobs", keyword, locationFilter, jobType],
    queryFn: () => backendApi.jobs.getAll({
      keyword: keyword || undefined,
      location: locationFilter || undefined,
      type: jobType !== "all" ? jobType : undefined
    }),
    keepPreviousData: true
  });

  const handleApply = (jobId) => {
    navigate(`/apply/${jobId}`);
  };

  const [savedJobs, setSavedJobs] = useState(new Set());
  const toggleSave = (id) => {
    const next = new Set(savedJobs);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSavedJobs(next);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-20">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* SIDEBAR FILTERS */}
          <aside className="w-full lg:w-80 shrink-0">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 sticky top-24 shadow-sm">
              <div className="flex items-center gap-2 mb-6">
                <Filter className="w-5 h-5 text-[#2557a7]" />
                <h2 className="font-bold text-slate-900">Filters</h2>
              </div>

              <div className="space-y-6">
                {/* Keyword Search */}
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">
                    Job Title
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                      placeholder="Title or keywords"
                      className="pl-10 h-11 border-slate-200 focus:ring-[#2557a7] focus:border-[#2557a7]"
                    />
                  </div>
                </div>

                {/* Location Filter */}
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">
                    Location
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      value={locationFilter}
                      onChange={(e) => setLocationFilter(e.target.value)}
                      placeholder="City or state"
                      className="pl-10 h-11 border-slate-200 focus:ring-[#2557a7] focus:border-[#2557a7]"
                    />
                  </div>
                </div>

                {/* Job Type Filter */}
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">
                    Job Type
                  </label>
                  <Select value={jobType} onValueChange={setJobType}>
                    <SelectTrigger className="h-11 border-slate-200">
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      <SelectItem value="Full-time">Full-time</SelectItem>
                      <SelectItem value="Part-time">Part-time</SelectItem>
                      <SelectItem value="Remote">Remote</SelectItem>
                      <SelectItem value="Contract">Contract</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  variant="outline" 
                  className="w-full h-11 border-[#2557a7] text-[#2557a7] hover:bg-blue-50"
                  onClick={() => {
                    setKeyword("");
                    setLocationFilter("");
                    setJobType("all");
                  }}
                >
                  Reset Filters
                </Button>
              </div>
            </div>
          </aside>

          {/* MAIN CONTENT Area */}
          <main className="flex-1">
            <div className="mb-6 flex justify-between items-center">
              <h1 className="text-xl font-bold text-slate-900">
                {isLoading ? "Searching..." : `${jobs.length} jobs matching your criteria`}
              </h1>
            </div>

            {isLoading && !isFetching ? (
              <div className="flex flex-col items-center justify-center py-24 bg-white rounded-3xl border border-slate-100 shadow-sm">
                <Loader2 className="w-12 h-12 text-[#2557a7] animate-spin mb-4" />
                <p className="text-slate-500 font-medium tracking-tight">Finding the best opportunities for you...</p>
              </div>
            ) : jobs.length > 0 ? (
              <div className="space-y-4">
                {jobs.map((job) => (
                  <JobListItem 
                    key={job.id} 
                    job={job} 
                    onApply={handleApply}
                    isSaved={savedJobs.has(job.id)}
                    onToggleSave={toggleSave}
                  />
                ))}
                
                {jobs.length >= itemsPerPage && (
                  <div className="pt-8 text-center">
                    <Button variant="ghost" className="text-[#2557a7] font-bold hover:bg-blue-50 px-8 h-12 rounded-xl">
                      Show more jobs
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-slate-200 p-20 text-center shadow-sm">
                <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Search className="w-10 h-10 text-slate-300" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">No jobs found</h3>
                <p className="text-slate-500 max-w-sm mx-auto mb-8">
                  We couldn't find any jobs matching your current filters. Try broadening your search or resetting the filters.
                </p>
                <Button 
                  onClick={() => {
                    setKeyword("");
                    setLocationFilter("");
                    setJobType("all");
                  }}
                  className="bg-[#2557a7] hover:bg-[#1e4687] text-white px-8 h-12 rounded-xl shadow-lg shadow-blue-100"
                >
                  Clear all filters
                </Button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

function JobListItem({ job, onApply, isSaved, onToggleSave }) {
  // Description truncation helper
  const truncate = (text, length) => {
    if (!text) return "";
    return text.length > length ? text.substring(0, length) + "..." : text;
  };

  return (
    <div className="group bg-white rounded-2xl border border-slate-200 p-6 hover:border-[#2557a7] hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300 relative overflow-hidden">
      {/* Decorative accent for unread/new? */}
      {job.is_new && <div className="absolute top-0 left-0 w-1 h-full bg-[#2557a7]"></div>}
      
      <div className="flex gap-5">
        <div className="w-14 h-14 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0 shadow-sm overflow-hidden group-hover:scale-105 transition-transform duration-300">
          {job.company_logo ? (
            <img src={job.company_logo} alt={job.company} className="w-full h-full object-contain" />
          ) : (
            <Building2 className="w-7 h-7 text-slate-300" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start gap-4">
            <div>
              <h3 className="text-xl font-bold text-slate-900 group-hover:text-[#2557a7] transition-colors line-clamp-1">
                {job.title}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-slate-600 font-semibold text-sm hover:underline cursor-pointer">
                  {job.company}
                </span>
                <span className="text-slate-300">•</span>
                <span className="text-slate-500 text-sm flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" />
                  {job.location}
                </span>
              </div>
            </div>
            
            <button 
              onClick={(e) => { e.stopPropagation(); onToggleSave(job.id); }}
              className={`p-2 rounded-full transition-colors ${isSaved ? 'text-[#2557a7] bg-blue-50' : 'text-slate-300 hover:text-slate-400'}`}
            >
              <Bookmark className={`w-5 h-5 ${isSaved ? 'fill-current' : ''}`} />
            </button>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Badge variant="secondary" className="bg-slate-100 text-slate-700 font-semibold px-3 py-1 rounded-lg">
              <Briefcase className="w-3 h-3 mr-1.5" />
              {job.type || job.work_mode || "Full-time"}
            </Badge>
            {job.salary_max && (
              <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 font-semibold px-3 py-1 rounded-lg border border-emerald-100">
                <IndianRupee className="w-3 h-3 mr-1.5" />
                ₹{job.salary_min ? `${job.salary_min} - ` : ""}{job.salary_max} LPA
              </Badge>
            )}
            {job.posted_date && (
              <span className="text-xs text-slate-400 ml-auto flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {formatDistanceToNow(new Date(job.posted_date), { addSuffix: true })}
              </span>
            )}
          </div>

          <p className="mt-5 text-slate-600 text-sm leading-relaxed line-clamp-2">
            {truncate(job.description, 180)}
          </p>

          <div className="mt-6 flex items-center justify-between">
            <div className="flex gap-2">
              {job.skills?.slice(0, 3).map(skill => (
                <span key={skill} className="text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50 px-2.5 py-1 rounded-md border border-slate-100">
                  {skill}
                </span>
              ))}
            </div>
            
            <Button 
              onClick={() => onApply(job.id)}
              className="bg-[#2557a7] hover:bg-[#1e4687] text-white px-8 rounded-xl font-bold h-11 shadow-lg shadow-blue-100 group-hover:translate-x-1 transition-transform"
            >
              Apply Now
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}